/**
 * Permission Gate - Generic component for permission-based conditional rendering
 * Provides fine-grained access control at the component level
 */

import React from 'react';
import { useRole } from '../../contexts/RoleContext';
import { Permissions } from '../../contexts/RoleContext';

export const PermissionGate = ({ 
  children, 
  permission, 
  permissions = [], 
  requireAll = false,
  role,
  roles = [],
  context = null,
  fallback = null,
  showFallback = false,
  debug = false 
}) => {
  const {
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    hasRole,
    hasAnyRole,
    userRole,
    isAuthenticated
  } = useRole();

  // Debug logging
  if (debug) {
    console.log('PermissionGate Debug:', {
      userRole,
      isAuthenticated,
      permission,
      permissions,
      role,
      roles,
      context
    });
  }

  // Must be authenticated for permission checks
  if (!isAuthenticated) {
    if (debug) console.log('PermissionGate: Not authenticated');
    return showFallback ? fallback : null;
  }

  let hasAccess = true;

  // Check single permission
  if (permission) {
    if (!hasPermission(permission)) {
      hasAccess = false;
      if (debug) console.log(`PermissionGate: Missing permission: ${permission}`);
    }
  }

  // Check multiple permissions
  if (permissions.length > 0) {
    if (requireAll) {
      if (!hasAllPermissions(permissions)) {
        hasAccess = false;
        if (debug) console.log(`PermissionGate: Missing required permissions: ${permissions.join(', ')}`);
      }
    } else {
      if (!hasAnyPermission(permissions)) {
        hasAccess = false;
        if (debug) console.log(`PermissionGate: Missing any of permissions: ${permissions.join(', ')}`);
      }
    }
  }

  // Check single role
  if (role) {
    if (!hasRole(role)) {
      hasAccess = false;
      if (debug) console.log(`PermissionGate: Missing role: ${role}`);
    }
  }

  // Check multiple roles
  if (roles.length > 0) {
    if (!hasAnyRole(roles)) {
      hasAccess = false;
      if (debug) console.log(`PermissionGate: Missing any of roles: ${roles.join(', ')}`);
    }
  }

  // Context-specific checks could be added here
  // For example, checking if user can access specific club, program, etc.

  if (debug) {
    console.log(`PermissionGate: Access ${hasAccess ? 'GRANTED' : 'DENIED'}`);
  }

  if (!hasAccess) {
    return showFallback ? fallback : null;
  }

  return <>{children}</>;
};

// Convenience components for common permission patterns

export const CanCreateClubs = ({ children, fallback = null }) => (
  <PermissionGate permission={Permissions.PLATFORM_CLUBS_CREATE} fallback={fallback}>
    {children}
  </PermissionGate>
);

export const CanManageClubs = ({ children, fallback = null }) => (
  <PermissionGate 
    permissions={[
      Permissions.PLATFORM_CLUBS_READ,
      Permissions.PLATFORM_CLUBS_UPDATE,
      Permissions.PLATFORM_CLUBS_DELETE
    ]} 
    fallback={fallback}
  >
    {children}
  </PermissionGate>
);

export const CanCreatePrograms = ({ children, fallback = null }) => (
  <PermissionGate permission={Permissions.CLUB_PROGRAMS_CREATE} fallback={fallback}>
    {children}
  </PermissionGate>
);

export const CanManagePrograms = ({ children, fallback = null }) => (
  <PermissionGate 
    permissions={[
      Permissions.CLUB_PROGRAMS_READ,
      Permissions.CLUB_PROGRAMS_UPDATE,
      Permissions.CLUB_PROGRAMS_DELETE
    ]} 
    fallback={fallback}
  >
    {children}
  </PermissionGate>
);

export const CanEvaluatePlayers = ({ children, fallback = null }) => (
  <PermissionGate permission={Permissions.COACH_PLAYERS_EVALUATE} fallback={fallback}>
    {children}
  </PermissionGate>
);

export const CanCreateAssessments = ({ children, fallback = null }) => (
  <PermissionGate permission={Permissions.COACH_ASSESSMENTS_CREATE} fallback={fallback}>
    {children}
  </PermissionGate>
);

export const CanManageUsers = ({ children, fallback = null }) => (
  <PermissionGate 
    permissions={[
      Permissions.CLUB_USERS_CREATE,
      Permissions.CLUB_USERS_READ,
      Permissions.CLUB_USERS_UPDATE
    ]} 
    fallback={fallback}
  >
    {children}
  </PermissionGate>
);

export const CanRegisterPrograms = ({ children, fallback = null }) => (
  <PermissionGate permission={Permissions.PARENT_PROGRAMS_REGISTER} fallback={fallback}>
    {children}
  </PermissionGate>
);

export const CanViewReports = ({ children, fallback = null }) => (
  <PermissionGate permission={Permissions.CLUB_REPORTS_READ} fallback={fallback}>
    {children}
  </PermissionGate>
);

// Higher-order component for permission-based rendering
export const withPermissions = (Component, permissionConfig) => {
  return function PermissionWrappedComponent(props) {
    return (
      <PermissionGate {...permissionConfig}>
        <Component {...props} />
      </PermissionGate>
    );
  };
};

// Hook for conditional logic based on permissions
export const usePermissionGate = () => {
  const roleUtils = useRole();
  
  return {
    ...roleUtils,
    
    // Helper function to check if user can perform an action
    can: (permission) => roleUtils.hasPermission(permission),
    
    // Helper function to check if user has any of the permissions
    canAny: (permissions) => roleUtils.hasAnyPermission(permissions),
    
    // Helper function to check if user has all permissions
    canAll: (permissions) => roleUtils.hasAllPermissions(permissions),
    
    // Context-aware permission checks
    canInContext: (permission, context = null) => {
      // For now, just check the basic permission
      // In the future, this could include context-specific logic
      return roleUtils.hasPermission(permission);
    },
    
    // Get all permissions for current user
    getAllPermissions: () => {
      const { userRole } = roleUtils;
      const rolePermissions = {
        platform_admin: [
          Permissions.PLATFORM_CLUBS_CREATE,
          Permissions.PLATFORM_CLUBS_READ,
          Permissions.PLATFORM_CLUBS_UPDATE,
          Permissions.PLATFORM_CLUBS_DELETE,
          Permissions.PLATFORM_ADMINS_CREATE,
          Permissions.PLATFORM_SYSTEM_MONITOR
        ],
        club_admin: [
          Permissions.CLUB_USERS_CREATE,
          Permissions.CLUB_USERS_READ,
          Permissions.CLUB_USERS_UPDATE,
          Permissions.CLUB_PROGRAMS_CREATE,
          Permissions.CLUB_PROGRAMS_READ,
          Permissions.CLUB_PROGRAMS_UPDATE,
          Permissions.CLUB_PROGRAMS_DELETE,
          Permissions.CLUB_BATCHES_CREATE,
          Permissions.CLUB_BATCHES_READ,
          Permissions.CLUB_BATCHES_UPDATE,
          Permissions.CLUB_REGISTRATIONS_READ,
          Permissions.CLUB_REGISTRATIONS_UPDATE,
          Permissions.CLUB_REPORTS_READ,
          Permissions.CLUB_SETTINGS_UPDATE
        ],
        coach: [
          Permissions.COACH_PROGRAMS_READ,
          Permissions.COACH_BATCHES_READ,
          Permissions.COACH_BATCHES_UPDATE,
          Permissions.COACH_PLAYERS_READ,
          Permissions.COACH_PLAYERS_EVALUATE,
          Permissions.COACH_REGISTRATIONS_READ,
          Permissions.COACH_REGISTRATIONS_PROCESS,
          Permissions.COACH_ASSESSMENTS_CREATE,
          Permissions.COACH_ASSESSMENTS_READ,
          Permissions.COACH_ASSESSMENTS_UPDATE
        ],
        parent: [
          Permissions.PARENT_PROFILE_READ,
          Permissions.PARENT_PROFILE_UPDATE,
          Permissions.PARENT_CHILDREN_CREATE,
          Permissions.PARENT_CHILDREN_READ,
          Permissions.PARENT_CHILDREN_UPDATE,
          Permissions.PARENT_PROGRAMS_REGISTER,
          Permissions.PARENT_PROGRAMS_READ,
          Permissions.PARENT_ASSESSMENTS_READ
        ],
        player: [
          Permissions.PLAYER_PROFILE_READ,
          Permissions.PLAYER_PROFILE_UPDATE,
          Permissions.PLAYER_PROGRAMS_READ,
          Permissions.PLAYER_ASSESSMENTS_READ
        ]
      };
      
      return rolePermissions[userRole] || [];
    }
  };
};